import { ClassData, MarkSet, Student, Grade, GradeScale, GradeScaleBand, Assessment, PopQuiz } from '../types';

/** What a quiz/test is called to students. Uses the teacher-chosen displayLabel, else
 *  falls back to the behaviour kind ('Test' / 'Pop Quiz'). */
export const popQuizLabel = (q: Pick<PopQuiz, 'displayLabel' | 'kind'>): string =>
  q.displayLabel?.trim() || (q.kind === 'test' ? 'Test' : 'Pop Quiz');

/**
 * Count assessments as ASSIGNMENTS. A multi-category assignment is stored as several
 * assessment records sharing one `bundleId` (e.g. a project marked on K/A/T/C = 4 records);
 * those must count as ONE assignment, not four. Each non-bundled assessment counts once.
 * Filter the list first (drafts, hidden, etc.) if you need a scoped count.
 */
export const countAssignments = (assessments: Assessment[]): number => {
  const seenBundles = new Set<string>();
  let n = 0;
  for (const a of assessments) {
    if (a.bundleId) {
      if (!seenBundles.has(a.bundleId)) { seenBundles.add(a.bundleId); n++; }
    } else {
      n++;
    }
  }
  return n;
};

/**
 * Collapse a list to one representative assessment per assignment: a bundle collapses to
 * its first-seen member; non-bundled assessments pass through. Use when you need to iterate
 * assignments (not per-category rows) — e.g. a "missing assignments" list.
 */
export const dedupeAssignments = (assessments: Assessment[]): Assessment[] => {
  const seenBundles = new Set<string>();
  const out: Assessment[] = [];
  for (const a of assessments) {
    if (a.bundleId) {
      if (!seenBundles.has(a.bundleId)) { seenBundles.add(a.bundleId); out.push(a); }
    } else {
      out.push(a);
    }
  }
  return out;
};

/**
 * Per-ASSIGNMENT "missing work" list, with multi-category bundles collapsed to one item.
 * A non-bundled assessment is missing iff `isMissing(a)`. A bundle (all records sharing a
 * bundleId) is missing ONLY when EVERY component is missing — a partly-graded or
 * partly-submitted bundle is not "missing". Returns one representative (first-seen
 * component) per missing assignment, preserving input order.
 *
 * Use everywhere a student/parent/teacher sees "missing work" (student grades view,
 * teacher per-student page, emails, at-risk flags) so a 3-category assignment reads as
 * ONE missing item, not three. Do NOT use for the gradebook grid, which is per-category.
 */
export const missingAssignmentsCollapsed = (
  assessments: Assessment[],
  isMissing: (a: Assessment) => boolean,
): Assessment[] => {
  const bundleComps = new Map<string, Assessment[]>();
  for (const a of assessments) {
    if (a.bundleId) {
      const arr = bundleComps.get(a.bundleId) || [];
      arr.push(a);
      bundleComps.set(a.bundleId, arr);
    }
  }
  const emittedBundles = new Set<string>();
  const out: Assessment[] = [];
  for (const a of assessments) {
    if (a.bundleId) {
      if (emittedBundles.has(a.bundleId)) continue;
      emittedBundles.add(a.bundleId);
      const comps = bundleComps.get(a.bundleId)!;
      if (comps.every(isMissing)) out.push(comps[0]);
    } else if (isMissing(a)) {
      out.push(a);
    }
  }
  return out;
};
// Student PII field lists live in their own zero-import file (so the netlify sweep
// can import them without dragging in React). Re-exported here for client callers.
export { STUDENT_PII_FIELDS, STUDENT_DROP_FIELDS } from './studentPiiFields';
import { sortByDisplayName, type NameResolver } from './vault/displayName';

// Given the embedded grades + releasedGrades maps and a key predicate, return the
// UNION of matching keys from both. Deletes must purge every subdoc whose embedded
// key is going away, and a key can live in releasedGrades without being in grades
// (e.g. a released grade later cleared) — deriving keys from grades alone would
// leave those releasedGrades subdocs orphaned. Pure (no firebase import) so it's
// unit-testable without booting the SDK.
export const removedGradeKeys = (
  classData: Pick<ClassData, 'grades' | 'releasedGrades'>,
  matches: (key: string) => boolean,
): string[] => {
  const keys = new Set<string>();
  for (const k of Object.keys(classData.grades || {})) if (matches(k)) keys.add(k);
  for (const k of Object.keys(classData.releasedGrades || {})) if (matches(k)) keys.add(k);
  return Array.from(keys);
};

// Parse a score that could be a number, fraction, or percentage
export const parseScore = (score: string | number | undefined, outOf: number): number => {
  if (score === undefined || score === '' || score === null) return NaN;
  
  const scoreStr = String(score).trim();
  
  // Handle percentage (e.g., "85%")
  if (scoreStr.endsWith('%')) {
    return parseFloat(scoreStr.slice(0, -1));
  }
  
  // Handle fraction (e.g., "17/20")
  if (scoreStr.includes('/')) {
    const [num, denom] = scoreStr.split('/').map(s => parseFloat(s.trim()));
    if (!isNaN(num) && !isNaN(denom) && denom !== 0) {
      return (num / denom) * 100;
    }
    return NaN;
  }
  
  // Handle raw number - convert to percentage based on outOf
  const num = parseFloat(scoreStr);
  if (!isNaN(num) && outOf > 0) {
    return (num / outOf) * 100;
  }
  
  return NaN;
};

/**
 * Format a stored grade as a "NN%" string, or return null when there is no
 * numeric mark to show (empty, non-numeric like a 'Y'/'N' completion mark, or a
 * zero/missing outOf). Callers render a soft "Ungraded" instead of an alarming
 * "NaN%". Keeps the scary red NaN off both teacher and student screens.
 */
export const formatGradePercent = (rawScore: unknown, outOf: number | undefined | null): string | null => {
  if (rawScore === undefined || rawScore === null || rawScore === '') return null;
  const n = typeof rawScore === 'number' ? rawScore : parseFloat(String(rawScore));
  if (!isFinite(n) || !outOf || outOf <= 0) return null;
  return `${Math.round((n / outOf) * 100)}%`;
};

// Calculate weighted average for a student across all categories
export const calculateAverage = (
  studentId: string, 
  classData: ClassData, 
  markSet: MarkSet
): number | null => {
  let totalWeightedScore = 0;
  let totalWeight = 0;
  
  for (const category of markSet.categories) {
    const categoryAssessments = classData.assessments.filter(
      a => a.categoryId === category.id && !a.isHidden && !a.isDraft && !a.isUngraded
    );
    
    if (categoryAssessments.length === 0) continue;
    
    let categoryTotal = 0;
    let categoryCount = 0;
    
    for (const assessment of categoryAssessments) {
      const gradeKey = `${studentId}-${assessment.id}`;
      const grade = classData.grades[gradeKey];
      
      if (grade && grade.rawScore !== '' && grade.rawScore !== undefined) {
        const pct = parseScore(grade.rawScore, assessment.outOf);
        if (!isNaN(pct)) {
          categoryTotal += pct * (assessment.weight || 1);
          categoryCount += (assessment.weight || 1);
        }
      }
    }
    
    if (categoryCount > 0) {
      const categoryAvg = categoryTotal / categoryCount;
      totalWeightedScore += categoryAvg * category.weight;
      totalWeight += category.weight;
    }
  }
  
  if (totalWeight === 0) return null;

  return Math.round(totalWeightedScore / totalWeight);
};

/**
 * The single source of truth for a student's FINAL mark to show/export.
 * Precedence: a teacher's absolute Hub Average override wins (released value,
 * then a pending draft); otherwise the KATC-weighted calculated average with any
 * teacher offset applied (clamped 0–100). Every consumer — report card, ARC/CSV
 * exports, AI comment context, and (via releasedAverages) the student + parent
 * portals — should use this so the number is identical everywhere.
 *
 * ── READ THIS BEFORE CALLING ────────────────────────────────────────────────
 * Two of the three inputs are OWNER-ONLY and live in `classes/{id}/private/roster`,
 * NOT on the class doc: `pendingHubAverage` and `studentHubAverageOffset` are both
 * in STUDENT_PII_FIELDS, so every writer strips them off the embedded students[].
 *
 * Call this with a student read straight from the class doc and you silently get
 * the UNADJUSTED mark — no error, just a different number from the one the teacher
 * sees. That is precisely how the offset used to reach every teacher-facing surface
 * and none of the student-facing ones.
 *
 * So: hydrate first — `hydrateStudentPrivate` on the client, `hydrateStudents`
 * (netlify/functions/shared/rosterHydrate.ts) on the server — or call
 * `computeReleasedAverages`, which takes the hydrated roster explicitly.
 */
export const getEffectiveAverage = (
  student: Student,
  classData: ClassData,
  markSet: MarkSet,
): number | null => {
  /**
   * PENDING FIRST.
   *
   * `pendingHubAverage` is the teacher's most recent intent, staged but not yet
   * released; `studentHubAverage` is what was released last time. The absolute
   * used to win, which meant a teacher typing 90 over a previously-released 85
   * saw the cell keep saying 85 — their number silently discarded — while the
   * gradebook's own display code (which preferred pending) said 90. Two answers
   * to "what is this student's mark" from one screen.
   *
   * It also broke the release itself: `releaseGrades` runs BEFORE the pending
   * draft is promoted, so with the absolute winning it published the OLD mark and
   * only then wrote the new one — leaving the student a release behind.
   */
  if (student.pendingHubAverage !== undefined && student.pendingHubAverage !== null) {
    return student.pendingHubAverage;
  }
  if (student.studentHubAverage !== undefined && student.studentHubAverage !== null) {
    return student.studentHubAverage;
  }
  const calculated = calculateAverage(student.id, classData, markSet);
  if (calculated === null) return null;
  const offset = student.studentHubAverageOffset || 0;
  return Math.max(0, Math.min(100, calculated + offset));
};

/**
 * The offset that makes `calculated` read as `typed`.
 *
 * This is what a teacher typing in the Hub Average box actually stores. Holding
 * the DIFFERENCE rather than the number is the whole point: a mark set two above
 * the calculated average stays two above as work comes in, in both directions,
 * instead of freezing on the day it was typed.
 *
 * Returns null when there is nothing to store — no calculated average to offset
 * from, or a typed value equal to it (offset 0 is not an override, and storing it
 * would light the "adjusted" flag on a mark nobody adjusted).
 */
export const hubOffsetFor = (typed: number, calculated: number | null): number | null => {
  if (calculated === null || !Number.isFinite(typed)) return null;
  const offset = Math.round(typed) - calculated;
  return offset === 0 ? null : offset;
};

/**
 * The `releasedAverages` map — the ONLY final mark students and parents ever read.
 *
 * They cannot read the owner-only mark set, so they cannot recompute a weighted
 * average; this plain studentId → number map, written at release, is what their
 * portals display. It is also the reason a teacher's adjustment can be invisible:
 * an adjusted 88 lands here as the number 88, carrying no trace of the 86 it came
 * from or of the fact that anyone touched it.
 *
 * `students` is passed SEPARATELY from `classData` on purpose. classData here is
 * usually straight off the class doc — where the offset and the pending draft have
 * been stripped — while `students` must be the HYDRATED roster. Two parameters
 * that look redundant, so that the hydration is impossible to forget silently.
 */
export const computeReleasedAverages = (
  students: Student[],
  classData: ClassData,
  markSet: MarkSet,
  opts: {
    /**
     * Whether a staged `pendingHubAverage` draft may be published.
     *
     * ONLY the grade-release flow may say true, and only because that same action
     * promotes the draft to `studentHubAverage` a moment later — the two have to
     * agree or the student's mark jumps at the next release.
     *
     * Every other writer must say false. Releasing a TEST's results is a different
     * action that promotes nothing, and it was publishing teachers' unreleased
     * drafts to students and parents as a side effect — then suppressing the
     * "unreleased changes" banner, because the released map now matched, so the
     * teacher was never told the draft had gone out.
     */
    includePendingDrafts: boolean;
  },
): Record<string, number> => {
  const averages: Record<string, number> = {};
  for (const student of students) {
    const source = opts.includePendingDrafts
      ? student
      : ({ ...student, pendingHubAverage: undefined } as Student);
    const avg = getEffectiveAverage(source, classData, markSet);
    if (avg !== null) averages[student.id] = avg;
  }
  return averages;
};

// Calculate average for a specific category
export const calculateCategoryAverage = (
  studentId: string,
  categoryId: string,
  classData: ClassData,
  opts?: { excludeCulminating?: boolean },
): number | null => {
  const categoryAssessments = classData.assessments.filter(
    a => a.categoryId === categoryId && !a.isHidden && !a.isDraft && !a.isUngraded
      && !(opts?.excludeCulminating && a.isCulminating)
  );
  
  if (categoryAssessments.length === 0) return null;
  
  let total = 0;
  let count = 0;
  
  for (const assessment of categoryAssessments) {
    const gradeKey = `${studentId}-${assessment.id}`;
    const grade = classData.grades[gradeKey];
    
    if (grade && grade.rawScore !== '' && grade.rawScore !== undefined) {
      const pct = parseScore(grade.rawScore, assessment.outOf);
      if (!isNaN(pct)) {
        total += pct * (assessment.weight || 1);
        count += (assessment.weight || 1);
      }
    }
  }
  
  if (count === 0) return null;
  return Math.round(total / count);
};

// Get color class based on grade percentage
export const getGradeColor = (percentage: number | null): string => {
  if (percentage === null) return 'text-gray-400';
  if (percentage >= 80) return 'text-emerald-600';
  if (percentage >= 70) return 'text-blue-600';
  if (percentage >= 60) return 'text-yellow-600';
  if (percentage >= 50) return 'text-orange-600';
  return 'text-red-600';
};

// ─── Grade output scale (DISPLAY layer) ──────────────────────────────────────
// Default band tables per scale type, used by formatGrade() when a scale has no
// explicit `bands`. Percent needs none. All tables are DESCENDING by `min` and
// bottom out at 0 so every 0–100 percent always lands in exactly one band.
export const DEFAULT_SCALE_BANDS: Record<Exclude<GradeScale['type'], 'percent'>, GradeScaleBand[]> = {
  letter: [
    { min: 90, label: 'A' },
    { min: 80, label: 'B' },
    { min: 70, label: 'C' },
    { min: 60, label: 'D' },
    { min: 0,  label: 'F' },
  ],
  gpa: [
    { min: 90, label: 'A', gpa: 4.0 },
    { min: 80, label: 'B', gpa: 3.0 },
    { min: 70, label: 'C', gpa: 2.0 },
    { min: 60, label: 'D', gpa: 1.0 },
    { min: 0,  label: 'F', gpa: 0.0 },
  ],
  // Ontario achievement levels.
  levels: [
    { min: 80, label: 'Level 4' },
    { min: 70, label: 'Level 3' },
    { min: 60, label: 'Level 2' },
    { min: 50, label: 'Level 1' },
    { min: 0,  label: 'R' },
  ],
};

// Resolve the bands to use for a scale: explicit bands win, else the per-type
// default. Returned already sorted DESCENDING by min so matching is a simple
// first-hit scan.
const resolveBands = (scale: GradeScale): GradeScaleBand[] => {
  // Guard against a Firestore scale.type outside the four known keys: the default
  // lookup would be `undefined` and `[...undefined]` throws inside a render. `?? []`
  // degrades to the existing "no band → percent" fallback instead of crashing.
  const bands = (scale.bands && scale.bands.length > 0)
    ? scale.bands
    : (scale.type === 'percent' ? [] : (DEFAULT_SCALE_BANDS[scale.type] ?? []));
  return [...bands].sort((a, b) => b.min - a.min);
};

// The band whose inclusive `min` floor is the highest one <= percent. Falls back
// to the lowest band if the percent sits below every floor (shouldn't happen with
// well-formed 0-anchored tables, but keeps output stable if it does).
const bandFor = (percent: number, bands: GradeScaleBand[]): GradeScaleBand | null => {
  if (bands.length === 0) return null;
  for (const b of bands) {
    if (percent >= b.min) return b;
  }
  return bands[bands.length - 1];
};

/**
 * THE single place that turns an internal percent into a display string.
 *  - percent null → 'N/A'
 *  - no scale / type 'percent' → "{round}%" (byte-identical to the old behaviour)
 *  - 'letter' | 'levels' → the matching band's label
 *  - 'gpa' → the matching band's gpa number, one decimal (e.g. "3.7"); "N/A" if the
 *    band carries no gpa (a misconfigured table).
 */
export const formatGrade = (percent: number | null, scale?: GradeScale): string => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) return 'N/A';
  if (!scale || scale.type === 'percent') return `${Math.round(percent)}%`;

  const band = bandFor(percent, resolveBands(scale));
  if (!band) return `${Math.round(percent)}%`;

  if (scale.type === 'gpa') {
    return typeof band.gpa === 'number' ? band.gpa.toFixed(1) : 'N/A';
  }
  return band.label;
};

/**
 * Color class for a grade, given the (optional) scale. Color thresholds track the
 * underlying PERCENT (a "B" is still 80–89), so this reuses getGradeColor for
 * every scale type. Kept as a named seam so a future scale could override colors
 * without touching call sites.
 */
export const gradeColorFor = (percentage: number | null, _scale?: GradeScale): string => {
  return getGradeColor(percentage);
};

/**
 * Sort students by last name, then first name, preferring a real name when one is available.
 *
 * Delegates to `sortByDisplayName` so there is ONE comparator rather than two that can disagree. Pass a
 * `resolve` from `useNameResolver` in a vaulted class: without it this sorts by the legacy roster name and
 * the pseudonym, exactly as it always has, so every existing caller is unaffected.
 *
 * A vaulted class sorted WITHOUT the resolver orders rows by pseudonym while displaying real names — Maya
 * above Noah above Chloé — which to a teacher scanning a roster is no order at all.
 */
export const sortStudentsByName = (
  students: Student[],
  opts: { resolve?: NameResolver } = {},
): Student[] => sortByDisplayName(students, opts);

// Format a date string for display
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format a date and time for display
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// Calculate class statistics
export const calculateClassStats = (classData: ClassData, markSet: MarkSet): { average: string; median: string } => {
  const averages: number[] = [];
  
  for (const student of classData.students) {
    const avg = calculateAverage(student.id, classData, markSet);
    if (avg !== null) {
      averages.push(avg);
    }
  }
  
  if (averages.length === 0) {
    return { average: 'N/A', median: 'N/A' };
  }
  
  const sum = averages.reduce((a, b) => a + b, 0);
  const avg = Math.round(sum / averages.length);
  
  const sorted = [...averages].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 !== 0 
    ? sorted[mid] 
    : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
  
  return { average: `${avg}`, median: `${median}` };
};

// Debounce utility - delays execution until after wait ms have elapsed since last call
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void; flush: () => void } => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;

  const debounced = (...args: Parameters<T>) => {
    lastArgs = args;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      if (lastArgs) {
        func(...lastArgs);
        lastArgs = null;
      }
      timeoutId = null;
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
      lastArgs = null;
    }
  };

  debounced.flush = () => {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      func(...lastArgs);
      timeoutId = null;
      lastArgs = null;
    }
  };

  return debounced;
};

// Grade queue for batching multiple grade updates into single Firebase write
export class GradeQueue {
  private pendingGrades: Map<string, { studentId: string; assessmentId: string; score: string | number }> = new Map();
  private flushTimeout: ReturnType<typeof setTimeout> | null = null;
  private flushCallback: ((grades: Array<{ studentId: string; assessmentId: string; score: string | number }>) => void) | null = null;
  private debounceMs: number;

  constructor(debounceMs: number = 800) {
    this.debounceMs = debounceMs;
  }

  setFlushCallback(callback: (grades: Array<{ studentId: string; assessmentId: string; score: string | number }>) => void) {
    this.flushCallback = callback;
  }

  enqueue(studentId: string, assessmentId: string, score: string | number) {
    const key = `${studentId}-${assessmentId}`;
    this.pendingGrades.set(key, { studentId, assessmentId, score });

    // Reset the flush timer
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }

    this.flushTimeout = setTimeout(() => this.flush(), this.debounceMs);
  }

  flush() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }

    if (this.pendingGrades.size > 0 && this.flushCallback) {
      const grades = Array.from(this.pendingGrades.values());
      this.pendingGrades.clear();
      this.flushCallback(grades);
    }
  }

  hasPending(): boolean {
    return this.pendingGrades.size > 0;
  }

  getPendingCount(): number {
    return this.pendingGrades.size;
  }
}
/**
 * Are two mark-set category lists the same GRADING CONFIGURATION?
 *
 * Compares the fields that actually affect a mark, field by field and in order —
 * deliberately NOT JSON.stringify, which is sensitive to key order. Firestore
 * returns map keys lexicographically sorted while locally-built objects keep
 * literal order, so a stringify compare reports "different" for content-identical
 * lists. That matters because the co-teacher mark-set self-heal WRITES on every
 * reported difference, and the write updates the class doc, which re-fires the
 * live subscription, which re-runs the check — a permanent mismatch is an
 * infinite write loop.
 */
export const sameMarkSetCategories = (
  a: Array<{ id?: string; name?: string; weight?: number; color?: string; description?: string }> | undefined,
  b: Array<{ id?: string; name?: string; weight?: number; color?: string; description?: string }> | undefined,
): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((cat, i) => {
    const other = b[i];
    return !!other
      && (cat?.id ?? '') === (other.id ?? '')
      && (cat?.name ?? '') === (other.name ?? '')
      && (cat?.weight ?? 0) === (other.weight ?? 0)
      && (cat?.color ?? '') === (other.color ?? '')
      // description is compared because it FEEDS THE AI (report comments, marking,
      // the tutor). It doesn't change a mark, but the write-skip guard means an
      // uncompared field is a field that silently stops reaching co-teachers.
      && (cat?.description ?? '') === (other.description ?? '');
  });
};
